
interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string,
        type?: string,
        title?: string,
        description?: string,
        url?: string,
        site_name?: string,
        updated_time?: string,
        image?: string,
        image_secure_url?: string,
        image_width?: number,
        image_height?: number,
        image_alt?: string,
        image_type?: string,
        video?: string,
        video_duration?: number,
        video_upload_date?: string,
        video_allow_embed?: boolean,
    };
    twitter?: {
        card?: string,
        title?: string,
        description?: string,
        image?: string,
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface ingredients {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    // aftercareTips: ListSection;
    faqData: FAQSection;
    ingredients: ingredients;
    // treatmentAreas1: TreatmentArea[];
    // treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string,
        beforeAfter: string[] | any,
        introductionImage?: string,
    };
    product: {
        id: number,
        title: string,
        price: number,
        description: string,
    };
}

export const antistressiv: {
    en: LanguageContent,
    ar: LanguageContent,
    shared: SharedData,
} =
{
    "en": {
        "url": "anti-stress-iv-drip",
        "meta": {
            "title": "Anti-Stress IV Drip in Dubai - Elite Body Home",
            "description": "Combat stress and fatigue with our Anti-Stress IV Drip in Dubai. This advanced intravenous therapy delivers essential vitamins and minerals directly to your bloodstream for immediate relaxation and long-term wellbeing.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/services/anti-stress-iv-drip-in-dubai/",
            "og": {
                "locale": "en_US",
                "type": "article",
                "title": "Anti-Stress IV Drip in Dubai - Elite Body Home",
                "description": "Combat stress and fatigue with our Anti-Stress IV Drip in Dubai. This advanced intravenous therapy delivers essential vitamins and minerals directly to your bloodstream.",
                "url": "https://elitebodyhome.com/services/anti-stress-iv-drip-in-dubai/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2025-01-23T09:27:39+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/iv-drip-treatment.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/iv-drip-treatment.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "Anti-Stress IV Drip Treatment in Dubai",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "Anti-Stress IV Drip in Dubai - Elite Body Home",
                "description": "Combat stress and fatigue with our Anti-Stress IV Drip in Dubai. This advanced intravenous therapy delivers essential vitamins and minerals directly to your bloodstream.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/iv-drip-treatment.jpg"
            }
        },
        "breadcrumbs": ["Home", "Services", "Anti-Stress IV Drip"],
        "hero": {
            "title": "Anti-Stress IV Drip in Dubai",
            "subtitle": "Relax, Recharge, and Rebalance!",
            "description": "Stress is a constant in today's fast-paced world. Our Anti-Stress IV Drip helps you combat stress, regain balance, and improve your health quickly. Administered safely and directly into your bloodstream, this advanced intravenous therapy delivers essential vitamins, minerals and antioxidants to help you feel calm and relaxed immediately, with long-term benefits for both body and mind."
        },
        "howItWorks": {
            "title": "What is Anti-Stress IV Drip?",
            "description": "The Anti-Stress IV Drip is designed to help prevent chronic stress by supplying your body with important nutrients. It offers a solution for individuals struggling with daily stress and fatigue. The drip consists of a powerful mixture of B vitamins, magnesium, vitamin C and amino acids that support adrenal health, boost energy levels, and improve mood."
        },
        "procedureSteps": {
            "heading": "Anti-Stress IV Drip Procedure",
            "para": "This relaxing treatment is administered by medical professionals in a controlled setting, providing quick relief from stress symptoms.",
            "steps": [
                {
                    "title": "Consultation",
                    "desc": "A medical professional evaluates your health and stress levels to ensure the treatment is appropriate for you.",
                    "icon": "fa-solid fa-comment-medical"
                },
                {
                    "title": "IV Insertion",
                    "desc": "A sterile IV catheter is placed into a vein in your arm, and the nutrient-rich solution is gradually infused over 30-45 minutes.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "Relaxation",
                    "desc": "You sit comfortably as the solution works through your system to reduce stress and improve mood.",
                    "icon": "fa-solid fa-couch"
                },
                {
                    "title": "Post-Treatment",
                    "desc": "Most patients feel instantly calmer, more focused and revitalized after the session.",
                    "icon": "fa-solid fa-face-smile"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits of Anti-Stress IV Drip",
            "subheading": "Experience multiple benefits from this powerful stress-relief treatment",
            "para": "The Anti-Stress IV Drip provides fast relief from stress and its symptoms by delivering nutrients directly to your bloodstream.",
            "items": [
                {
                    "title": "Rapid Stress Relief",
                    "desc": "Nutrients bypass digestion and go straight into your bloodstream, providing instant relaxation and decreased anxiety.",
                    "icon": "fa-solid fa-heart-circle-check"
                },
                {
                    "title": "Improved Sleep Quality",
                    "desc": "Magnesium calms the nervous system and relaxes muscles, promoting better sleep.",
                    "icon": "fa-solid fa-moon"
                },
                {
                    "title": "Increased Energy",
                    "desc": "B vitamins support cellular energy production, reducing feelings of fatigue and burnout.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "Enhanced Immunity",
                    "desc": "Vitamin C strengthens your immune response to help prevent illness.",
                    "icon": "fa-solid fa-shield-virus"
                }
            ]
        },
        "ingredients": {
            "heading": "Key Ingredients",
            "subheading": "Powerful nutrients for stress relief and wellbeing",
            "para": "Our Anti-Stress IV Drip contains a carefully balanced formula of essential vitamins and minerals:",
            "items": [
                {
                    "title": "B Vitamins Complex",
                    "desc": "Essential for energy metabolism and optimal brain function.",
                    "icon": "fa-solid fa-brain"
                },
                {
                    "title": "Magnesium",
                    "desc": "Aids muscle relaxation, calms the nervous system, and improves sleep quality.",
                    "icon": "fa-solid fa-bed"
                },
                {
                    "title": "Vitamin C",
                    "desc": "Powerful antioxidant that fights free radicals and supports immunity.",
                    "icon": "fa-solid fa-shield-heart"
                },
                {
                    "title": "Amino Acids",
                    "desc": "Support tissue healing and muscle relaxation for overall wellbeing.",
                    "icon": "fa-solid fa-dna"
                }
            ]
        },
        "eligibility": {
            "heading": "Who Can Benefit?",
            "subheading": "Ideal candidates for Anti-Stress IV Therapy",
            "para": "The Anti-Stress IV Drip is perfect for those experiencing high stress levels, fatigue, anxiety or burnout. It may be especially beneficial for:",
            "items": [
                {
                    "title": "Busy Professionals",
                    "desc": "Those with demanding jobs or constant work pressure.",
                    "icon": "fa-solid fa-briefcase"
                },
                {
                    "title": "Chronic Fatigue",
                    "desc": "Individuals with anxiety or chronic fatigue syndrome.",
                    "icon": "fa-solid fa-cloud"
                },
                {
                    "title": "Poor Sleep",
                    "desc": "People suffering from insomnia or poor sleep quality.",
                    "icon": "fa-solid fa-moon"
                },
                {
                    "title": "Mental Clarity",
                    "desc": "Those wanting to enhance focus and emotional balance.",
                    "icon": "fa-solid fa-lightbulb"
                }
            ]
        },
        "preparationTips": {
            "heading": "Preparation Guidelines",
            "subheading": "How to prepare for your IV therapy session",
            "para": "For optimal results from your Anti-Stress IV Drip, we recommend these preparation steps:",
            "items": [
                {
                    "title": "Stay Hydrated",
                    "desc": "Drink plenty of water before your appointment to ease IV insertion and enhance treatment effects.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "Eat Light",
                    "desc": "Have a light, healthy meal beforehand to prevent dizziness during treatment.",
                    "icon": "fa-solid fa-utensils"
                },
                {
                    "title": "Comfortable Clothing",
                    "desc": "Wear loose-fitting clothes for comfort during the session.",
                    "icon": "fa-solid fa-shirt"
                },
                {
                    "title": "Medical History",
                    "desc": "Inform us about any medications or medical conditions beforehand.",
                    "icon": "fa-solid fa-file-medical"
                }
            ]
        },
        "results": {
            "heading": "Expected Results",
            "subheading": "What to expect after your Anti-Stress IV Drip",
            "para": "Patients typically experience noticeable benefits soon after treatment:",
            "items": [
                {
                    "title": "Immediate Calmness",
                    "desc": "Feelings of stress and anxiety typically diminish within an hour.",
                    "icon": "fa-solid fa-face-smile-beam"
                },
                {
                    "title": "Better Sleep",
                    "desc": "Improved sleep quality and duration for more restorative rest.",
                    "icon": "fa-solid fa-moon"
                },
                {
                    "title": "Energy Boost",
                    "desc": "Enhanced mental and physical energy to tackle daily challenges.",
                    "icon": "fa-solid fa-battery-full"
                },
                {
                    "title": "Mood Enhancement",
                    "desc": "Balanced neurotransmitters help stabilize mood and reduce overwhelm.",
                    "icon": "fa-solid fa-face-laugh-squint"
                }
            ]
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "Common questions about Anti-Stress IV Therapy",
            "items": [
                {
                    "question": "How quickly will I feel the effects?",
                    "answer": "Most patients begin feeling effects during the treatment, with full benefits typically felt within an hour after completion."
                },
                {
                    "question": "How long do the effects last?",
                    "answer": "Effects can last several days to a week. Regular sessions (weekly or bi-weekly) are recommended for sustained benefits."
                },
                {
                    "question": "Is there any downtime after treatment?",
                    "answer": "There's no downtime. You can resume normal activities immediately after your session."
                },
                {
                    "question": "How often should I get an Anti-Stress IV Drip?",
                    "answer": "Frequency depends on your stress levels and goals. Weekly sessions are ideal for high-stress periods, while monthly maintenance may suffice for general wellness."
                },
                {
                    "question": "Are there any side effects?",
                    "answer": "Side effects are rare but may include mild bruising at the injection site or a metallic taste during infusion. These are temporary and resolve quickly."
                },
                {
                    "question": "Can I combine this with other treatments?",
                    "answer": "Yes, our Anti-Stress IV Drip complements other wellness treatments. Consult with our specialists for personalized recommendations."
                }
            ]
        }
    },
    "ar": {
        "url": "anti-stress-iv-drip",
        "meta": {
            "title": "علاج التنقيط الوريدي المضاد للتوتر في دبي | إيليت بودي هوم",
            "description": "حارب التوتر والإرهاق مع علاج التنقيط الوريدي المضاد للتوتر في دبي. يوفر هذا العلاج المتقدم الفيتامينات والمعادن مباشرة إلى مجرى الدم للاسترخاء الفوري وتحسين الصحة على المدى الطويل.",
            "robots": "index, follow",
            "canonical": "https://elitebodyhome.com/services/anti-stress-iv-drip-in-dubai/",
            "og": {
                "title": "علاج التنقيط الوريدي المضاد للتوتر في دبي | إيليت بودي هوم",
                "description": "حارب التوتر والإرهاق مع علاج التنقيط الوريدي المضاد للتوتر في دبي. يوفر هذا العلاج المتقدم الفيتامينات والمعادن مباشرة إلى مجرى الدم.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/iv-drip-treatment.jpg"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "التنقيط الوريدي المضاد للتوتر"],
        "hero": {
            "title": "التنقيط الوريدي المضاد للتوتر في دبي",
            "subtitle": "استرخِ، استعد نشاطك، واستعد توازنك!",
            "description": "أصبح التوتر جزءاً لا مفر منه من يومنا في ظل إيقاع الحياة المتسارع. يساعدك علاج التنقيط الوريدي المضاد للتوتر في التغلب على التوتر، استعادة التوازن، وتحسين صحتك بسرعة. يُحقن هذا العلاج الحديث عبر الوريد بطريقة آمنة ليصل مباشرةً إلى مجرى الدم، ويوفر لجسمك مزيجاً فعالاً من الفيتامينات والمعادن ومضادات الأكسدة التي تحتاجها لتستعيد شعورك بالراحة والهدوء فوراً، مع فوائد طويلة المدى للجسم والعقل."
        },
        "howItWorks": {
            "title": "ما هو التنقيط الوريدي المضاد للتوتر؟",
            "description": "صُمم التنقيط الوريدي المضاد للتوتر لمواجهة آثار الإجهاد المزمن من خلال تجديد العناصر الغذائية الأساسية في الجسم. يُعد هذا العلاج مثالياً للأشخاص الذين يعانون من ضغوط الحياة اليومية، إذ يحتوي على مزيج فعال من فيتامين ب بأنواعه، والمغنيسيوم، وفيتامين سي، والأحماض الأمينية التي تدعم صحة الغدد الكظرية، وتحفز الطاقة، وتحسن المزاج."
        },
        "procedureSteps": {
            "heading": "خطوات علاج التنقيط الوريدي",
            "para": "يُقدَّم هذا العلاج المريح تحت إشراف أطباء متخصصين في بيئة طبية آمنة، ويوفر راحة سريعة من أعراض التوتر.",
            "steps": [
                {
                    "title": "الاستشارة",
                    "desc": "يقوم الطبيب بتقييم حالتك الصحية ومستوى التوتر لديك للتأكد من ملائمة العلاج لك.",
                    "icon": "fa-solid fa-comment-medical"
                },
                {
                    "title": "إدخال القسطرة",
                    "desc": "تُوضع قسطرة وريدية معقمة في أحد أوردة الذراع، ويُحقن المحلول الغني بالعناصر الغذائية ببطء على مدار 30-45 دقيقة.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "الاسترخاء",
                    "desc": "تستلقي بوضع مريح بينما يعمل المحلول على تقليل التوتر وتحسين المزاج.",
                    "icon": "fa-solid fa-couch"
                },
                {
                    "title": "ما بعد الجلسة",
                    "desc": "يشعر معظم المرضى فوراً بالهدوء، زيادة التركيز وتجدد الطاقة بعد الجلسة.",
                    "icon": "fa-solid fa-face-smile"
                }
            ]
        },
        "benefits": {
            "heading": "فوائد التنقيط الوريدي المضاد للتوتر",
            "subheading": "استمتع بفوائد متعددة من هذا العلاج الفعال لتخفيف التوتر",
            "para": "يوفر التنقيط الوريدي المضاد للتوتر راحة سريعة من أعراض التوتر من خلال إيصال العناصر الغذائية مباشرة إلى مجرى الدم.",
            "items": [
                {
                    "title": "تخفيف سريع للتوتر",
                    "desc": "تنتقل المغذيات مباشرةً إلى مجرى الدم دون المرور بمراحل الهضم، مما يوفر استرخاء فوري ويقلل القلق.",
                    "icon": "fa-solid fa-heart-circle-check"
                },
                {
                    "title": "تحسين جودة النوم",
                    "desc": "يساعد المغنيسيوم على تهدئة الجهاز العصبي وإرخاء العضلات، مما يعزز النوم الجيد.",
                    "icon": "fa-solid fa-moon"
                },
                {
                    "title": "زيادة الطاقة",
                    "desc": "يدعم فيتامين ب إنتاج الطاقة الخلوية، ويقلل الشعور بالإرهاق والإنهاك.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "تعزيز المناعة",
                    "desc": "يقوي فيتامين سي الاستجابة المناعية للمساعدة في الوقاية من الأمراض.",
                    "icon": "fa-solid fa-shield-virus"
                }
            ]
        },
        "ingredients": {
            "heading": "المكونات الرئيسية",
            "subheading": "عناصر غذائية قوية لتخفيف التوتر وتعزيز الصحة",
            "para": "يحتوي علاجنا على تركيبة متوازنة بعناية من الفيتامينات والمعادن الأساسية:",
            "items": [
                {
                    "title": "مجموعة فيتامين ب",
                    "desc": "ضروري لعملية التمثيل الغذائي للطاقة ووظائف الدماغ المثلى.",
                    "icon": "fa-solid fa-brain"
                },
                {
                    "title": "المغنيسيوم",
                    "desc": "يساعد على استرخاء العضلات، يهدئ الجهاز العصبي، ويحسن جودة النوم.",
                    "icon": "fa-solid fa-bed"
                },
                {
                    "title": "فيتامين سي",
                    "desc": "مضاد أكسدة قوي يحارب الجذور الحرة ويدعم المناعة.",
                    "icon": "fa-solid fa-shield-heart"
                },
                {
                    "title": "الأحماض الأمينية",
                    "desc": "تدعم التئام الأنسجة واسترخاء العضلات لتحسين الصحة العامة.",
                    "icon": "fa-solid fa-dna"
                }
            ]
        },
        "eligibility": {
            "heading": "من يمكنه الاستفادة؟",
            "subheading": "المرشحون المثاليون للعلاج الوريدي المضاد للتوتر",
            "para": "يُعد التنقيط الوريدي المضاد للتوتر مثاليًا لمن يعانون من مستويات عالية من التوتر أو الإرهاق أو القلق. قد يكون مفيدًا بشكل خاص لـ:",
            "items": [
                {
                    "title": "المهنيين المشغولين",
                    "desc": "أولئك الذين يعانون من ضغوط عمل مستمرة.",
                    "icon": "fa-solid fa-briefcase"
                },
                {
                    "title": "متلازمة الإرهاق المزمن",
                    "desc": "الأفراد الذين يعانون من القلق أو متلازمة الإرهاق المزمن.",
                    "icon": "fa-solid fa-cloud"
                },
                {
                    "title": "اضطرابات النوم",
                    "desc": "الأشخاص الذين يعانون من الأرق أو ضعف جودة النوم.",
                    "icon": "fa-solid fa-moon"
                },
                {
                    "title": "تحسين الوظائف العقلية",
                    "desc": "الراغبون في تعزيز التركيز والتوازن العاطفي.",
                    "icon": "fa-solid fa-lightbulb"
                }
            ]
        },
        "preparationTips": {
            "heading": "إرشادات التحضير",
            "subheading": "كيف تستعد لجلسة العلاج الوريدي",
            "para": "لتحقيق أفضل النتائج من علاج التنقيط الوريدي المضاد للتوتر، نوصي باتباع هذه الخطوات التحضيرية:",
            "items": [
                {
                    "title": "حافظ على ترطيب الجسم",
                    "desc": "اشرب الكثير من الماء قبل موعدك لتسهيل إدخال القسطرة وتعزيز تأثير العلاج.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "تناول وجبة خفيفة",
                    "desc": "تناول وجبة خفيفة وصحية مسبقًا لتجنب الدوخة أثناء العلاج.",
                    "icon": "fa-solid fa-utensils"
                },
                {
                    "title": "ملابس مريحة",
                    "desc": "ارتدي ملابس فضفاضة للراحة أثناء الجلسة.",
                    "icon": "fa-solid fa-shirt"
                },
                {
                    "title": "التاريخ الطبي",
                    "desc": "أخبرنا عن أي أدوية تتناولها أو حالات طبية تعاني منها مسبقًا.",
                    "icon": "fa-solid fa-file-medical"
                }
            ]
        },
        "results": {
            "heading": "النتائج المتوقعة",
            "subheading": "ما يمكن توقعه بعد جلسة التنقيط الوريدي",
            "para": "يشعر المرضى عادةً بفوائد ملحوظة بعد العلاج بوقت قصير:",
            "items": [
                {
                    "title": "هدوء فوري",
                    "desc": "تتلاشى مشاعر التوتر والقلق عادةً خلال ساعة.",
                    "icon": "fa-solid fa-face-smile-beam"
                },
                {
                    "title": "نوم أفضل",
                    "desc": "تحسن في جودة النوم ومدته لراحة أكثر فعالية.",
                    "icon": "fa-solid fa-moon"
                },
                {
                    "title": "زيادة الطاقة",
                    "desc": "تحسن في الطاقة الذهنية والبدنية لمواجهة التحديات اليومية.",
                    "icon": "fa-solid fa-battery-full"
                },
                {
                    "title": "تحسن المزاج",
                    "desc": "توازن الناقلات العصبية يساعد على استقرار المزاج وتقليل الشعور بالإرهاق.",
                    "icon": "fa-solid fa-face-laugh-squint"
                }
            ]
        },
        "faqData": {
            "heading": "الأسئلة الشائعة",
            "subheading": "أسئلة متداولة حول العلاج الوريدي المضاد للتوتر",
            "items": [
                {
                    "question": "ما هي سرعة الشعور بالتأثيرات؟",
                    "answer": "يبدأ معظم المرضى في الشعور بالتأثيرات أثناء العلاج، مع اكتمال الفوائد عادةً خلال ساعة بعد الانتهاء."
                },
                {
                    "question": "كم تدوم التأثيرات؟",
                    "answer": "يمكن أن تدوم التأثيرات عدة أيام إلى أسبوع. نوصي بجلسات منتظمة (أسبوعية أو كل أسبوعين) للحصول على فوائد مستدامة."
                },
                {
                    "question": "هل هناك فترة نقاهة بعد العلاج؟",
                    "answer": "لا توجد فترة نقاهة. يمكنك استئناف الأنشطة العادية مباشرة بعد الجلسة."
                },
                {
                    "question": "كم مرة يجب أن أحصل على التنقيط الوريدي؟",
                    "answer": "يعتمد التكرار على مستوى التوتر والأهداف. الجلسات الأسبوعية مثالية لفترات التوتر الشديد، بينما قد تكفي جلسة شهرية للصحة العامة."
                },
                {
                    "question": "هل هناك أي آثار جانبية؟",
                    "answer": "الآثار الجانبية نادرة ولكن قد تشمل كدمات طفيفة في موقع الحقن أو طعم معدني أثناء التسريب. هذه مؤقتة وتختفي بسرعة."
                },
                {
                    "question": "هل يمكنني الجمع بين هذا العلاج وعلاجات أخرى؟",
                    "answer": "نعم، يتكامل التنقيط الوريدي المضاد للتوتر مع علاجات الصحة الأخرى. استشر أخصائيينا للحصول على توصيات شخصية."
                }
            ]
        }
    },
    "shared": {
        "images": {
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/ffffff.jpg",
            "beforeAfter": [

            ]
        },
        "product": {
            "id": 30,
            "title": "Anti-Stress IV Drip Treatment",
            "description": "Advanced intravenous therapy delivering essential vitamins, minerals and antioxidants directly to your bloodstream for immediate stress relief and long-term wellbeing.",
            "price": 499
        }
    }
}